"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";

export function BrowserBanner({ onDismiss }: { onDismiss: () => void }) {
  const t = useTranslations("landing");
  const posthog = usePostHog();
  const shownAt = useRef(Date.now());

  useEffect(() => {
    posthog?.capture("browser_banner_shown");
  }, [posthog]);

  const handleDismiss = () => {
    posthog?.capture("browser_banner_dismissed", {
      duration_sec: Math.round((Date.now() - shownAt.current) / 1000),
    });
    onDismiss();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center bg-surface animate-fade-in overflow-y-auto">
      <div className="flex flex-col items-center px-6 py-8 pt-[calc(24px+env(safe-area-inset-top))] pb-[calc(24px+env(safe-area-inset-bottom))] w-full max-w-[400px]">
        {/* Screenshot */}
        <img
          src="/onboarding/browser-hint.jpeg"
          alt=""
          className="w-full max-w-[280px] rounded-2xl border border-line/50 shadow-xl shadow-black/20 mb-12"
          draggable={false}
        />

        {/* Text */}
        <h2 className="text-center text-2xl font-bold text-ink leading-tight">
          {t("browserBanner.title")}
        </h2>
        <p className="mt-3 text-center text-xl text-ink-mute leading-relaxed max-w-[320px]">
          {t("browserBanner.description")}
        </p>

        {/* Steps */}
        <div className="mt-10 flex w-full flex-col gap-2.5">
          <div className="flex items-center gap-4 rounded-2xl bg-surface-mute px-5 py-3.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-mint/20 text-sm font-bold text-mint">
              1
            </span>
            <span className="text-[15px] text-ink">{t("browserBanner.step1")}</span>
          </div>
          <div className="flex items-center gap-4 rounded-2xl bg-surface-mute px-5 py-3.5">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-mint/20 text-sm font-bold text-mint">
              2
            </span>
            <span className="text-[15px] text-ink">{t("browserBanner.step2")}</span>
          </div>
        </div>

        {/* Dismiss button */}
        <button
          className="mt-16 w-full rounded-xl border border-line bg-transparent px-8 py-3 text-sm font-medium text-ink-mute transition-all active:scale-[0.98] cursor-pointer"
          onClick={handleDismiss}
        >
          {t("browserBanner.dismiss")}
        </button>
      </div>
    </div>
  );
}
